import dayjs from 'dayjs'

import { ISO_DATE_TIME_FORMAT_Z } from './DateAndTimeUtils'

/**
 * Alerts are shown for the last 60 days by default.
 *
 * This mirrors the entity-service `alerts.default-lookback-days` (60). When a request carries no
 * alert date range, the backend applies that same window as a literal lower bound so the
 * `ubq.alert` hypertable prunes chunks instead of opening all of them (UBQ-5209). Pre-selecting the
 * range here makes that implicit backend window visible to the user, and keeps the alerts list
 * consistent with the bell count — which is deliberately always computed over the default window,
 * never over the user's picked range (see Intransit `bellParams`).
 *
 * Keep this in sync if `alerts.default-lookback-days` is changed in the backend config.
 */
export const DEFAULT_ALERT_LOOKBACK_DAYS = 60

/**
 * The default alert date range: `[60 days ago, now]`, in the same UTC ISO shape the date picker
 * emits (`DateRangeInput` → `ISO_DATE_TIME_FORMAT_Z`). The backend parses it with a strict
 * `Instant.parse`, so the trailing `Z` is required.
 *
 * Computed per call rather than at module load, so a long-lived tab does not hold an increasingly
 * stale window.
 */
export const getDefaultAlertDateRange = (): [string, string] => [
    dayjs()
        .subtract(DEFAULT_ALERT_LOOKBACK_DAYS, 'day')
        .utc()
        .format(ISO_DATE_TIME_FORMAT_Z),
    dayjs().utc().format(ISO_DATE_TIME_FORMAT_Z),
]

/**
 * True when a range is (still) the pre-selected default rather than something the user picked.
 *
 * Used so the default window does not count towards the active-filter badge — otherwise the alerts
 * screen would always claim one filter is applied and "Clear All Filters" could never return the
 * count to zero.
 *
 * Compared at day granularity on purpose: the default's end is "now", so the exact timestamps differ
 * between when the range was generated and when it is checked. Day granularity is stable for the
 * whole day; a session left open across UTC midnight will start counting it as a filter until the
 * next reset, which is harmless and self-correcting.
 */
export const isDefaultAlertDateRange = (
    range: [string | null, string | null],
): boolean => {
    const [start, end] = range
    if (!start || !end) return false

    const day = (value: string): string =>
        dayjs(value).utc().format('YYYY-MM-DD')
    const expectedStart = dayjs()
        .subtract(DEFAULT_ALERT_LOOKBACK_DAYS, 'day')
        .utc()
        .format('YYYY-MM-DD')
    const expectedEnd = dayjs().utc().format('YYYY-MM-DD')

    return day(start) === expectedStart && day(end) === expectedEnd
}
